import { creatorAuthUrl } from "@apis/http/api";
import { useSerializeCallback } from "@kalodata/tools";
import {
  TIKTOK_SHOP_AUTH_BROADCAST_CHANNEL,
  TIKTOK_SHOP_AUTH_RESULT,
  TiktokShopAuthCode,
  type TiktokShopAuthResult,
  type TiktokShopAuthResultMessage,
  TiktokShopAuthState,
} from "@type/tiktokShopAuth";
import { filter, firstValueFrom, fromEvent, interval, map, race, switchMap, timer } from "rxjs";

const POPUP_WIDTH = 1024;
const POPUP_HEIGHT = 720;

/** 兜底超时：远超用户正常授权耗时，仅用于极端场景下避免 promise 永挂 */
const POPUP_TIMEOUT_MS = 10 * 60 * 1000;
/** focus 启发式的"冷启动"窗口：弹窗刚开 + 父窗口未失焦的瞬间不参与判定，避免误伤 */
const HEURISTIC_GRACE_MS = 1500;
/** 父窗口拿到 focus 后等一小段时间，给在路上的成功消息让路 */
const FOCUS_DEBOUNCE_MS = 300;
/** 二次确认窗口：popup.closed 持续为 true 才认为真的关了 */
const SUSTAIN_CHECK_MS = 1000;
/** 兜底轮询间隔：当 focus 事件未触发时，定期检查 popup.closed */
const POLL_INTERVAL_MS = 500;

const toResult = (data: TiktokShopAuthResultMessage): TiktokShopAuthResult => {
  if (data.state === TiktokShopAuthState.Ok) {
    return { state: TiktokShopAuthState.Ok, code: TiktokShopAuthCode.Ok, msg: data.msg };
  }
  if (data.state === TiktokShopAuthState.Error) {
    return { state: TiktokShopAuthState.Error, code: TiktokShopAuthCode.BackendError, msg: data.msg };
  }
  return { state: TiktokShopAuthState.Error, code: TiktokShopAuthCode.UnknownState, msg: data.msg };
};

const userClosedResult = (): TiktokShopAuthResult => ({
  state: TiktokShopAuthState.Error,
  code: TiktokShopAuthCode.UserClosed,
});

const buildPopupFeatures = () => {
  const left = window.screenX + Math.max(0, (window.outerWidth - POPUP_WIDTH) / 2);
  const top = window.screenY + Math.max(0, (window.outerHeight - POPUP_HEIGHT) / 2);
  return ["popup=yes", `width=${POPUP_WIDTH}`, `height=${POPUP_HEIGHT}`, `left=${left}`, `top=${top}`].join(",");
};

export const useTiktokShopAuth = () => {
  const openTikTokAuth = useSerializeCallback(async (): Promise<TiktokShopAuthResult> => {
    const url = await creatorAuthUrl();
    if (!url || url.trim() === "") {
      return { state: TiktokShopAuthState.Error, code: TiktokShopAuthCode.GetAuthUrlFailed };
    }

    const popup = window.open(url, "_blank", buildPopupFeatures());
    if (!popup) {
      return { state: TiktokShopAuthState.Error, code: TiktokShopAuthCode.OpenWindowFailed };
    }

    const openedAt = Date.now();
    const channel = new BroadcastChannel(TIKTOK_SHOP_AUTH_BROADCAST_CHANNEL);

    const message$ = fromEvent<MessageEvent>(channel, "message").pipe(
      map((e) => e.data as TiktokShopAuthResultMessage | undefined),
      filter((d): d is TiktokShopAuthResultMessage => d?.type === TIKTOK_SHOP_AUTH_RESULT),
      map(toResult),
    );

    const userClosed$ = fromEvent(window, "focus").pipe(
      filter(() => Date.now() - openedAt > HEURISTIC_GRACE_MS),
      switchMap(() => timer(FOCUS_DEBOUNCE_MS)),
      filter(() => popup.closed),
      switchMap(() => timer(SUSTAIN_CHECK_MS).pipe(filter(() => popup.closed))),
      map(userClosedResult),
    );

    // 兜底：focus 事件可能永不触发（1.5s 内关闭被 grace 过滤、父窗口从未失焦等场景），
    // 用轮询保证 popup 关闭后总能在 ~SUSTAIN_CHECK_MS 内被识别
    const popping$ = interval(POLL_INTERVAL_MS).pipe(
      filter(() => popup.closed),
      switchMap(() => timer(SUSTAIN_CHECK_MS).pipe(filter(() => popup.closed))),
      map(userClosedResult),
    );

    const timeout$ = timer(POPUP_TIMEOUT_MS).pipe(map(userClosedResult));

    try {
      return await firstValueFrom(race(message$, userClosed$, popping$, timeout$));
    } finally {
      channel.close();
      try {
        if (!popup.closed) popup.close();
      } catch {
        // popup 引用在 COOP 隔离下可能已失效，关失败无害
      }
    }
  });

  return openTikTokAuth;
};
